import {
    Component,
    EventEmitter,
    Input,
    Output,
    TemplateRef,
    OnChanges,
    SimpleChanges,
    ViewChild,
    OnDestroy,
    AfterViewInit
} from '@angular/core';
import { ReplaySubject, fromEvent, Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { IonItemSliding } from '@ionic/angular';
import { filter, tap, takeUntil, take } from 'rxjs/operators';

import { ScrollService } from '../scroll-service/scroll-service';
import { getUniqueId, retry } from '../utils';
import { getValueFromTranslationSync } from '../language';

@Component({
    standalone: false,
    selector: 'abs-dynamic-list',
    template: `
        <ng-template #defaultTemplate let-item="item">
            <div class="itemName">
                <span>{{ item.name | language | async }}</span>
            </div>
        </ng-template>

        <ng-template #defaultHeaderTemplate let-item="item">
            <ion-label>{{ item.name }}</ion-label>
        </ng-template>

        <ng-container *ngIf="showSearch">
            <ion-searchbar (ionInput)="updateSearch($any($event).detail.value)"></ion-searchbar>
        </ng-container>
        <ion-list #virtualScroll [style.height]="(listHeight$ | async) + 'px'">
            <div
                class="row"
                *ngFor="let item of displayRows$ | async; let i = index; trackBy: trackByFn"
                [style.height]="item.height + 'px'"
                [style.transform]="'translate3d(0, ' + item.yPos + 'px, 0)'"
                [class.deleted]="item.deleted"
                [class.clickable]="itemHover"
            >
                <ion-item-divider *ngIf="item.isHeader && item.name">
                    <ng-template
                        [ngTemplateOutlet]="headerTemplate ?? defaultHeaderTemplate"
                        [ngTemplateOutletContext]="{ item: item }"
                    ></ng-template>
                </ion-item-divider>
                <ion-item-sliding *ngIf="!item.isHeader" #itemSliding (ionSwipe)="delete($event, item, itemSliding)">
                    <ion-item
                        *ngIf="getRouterLink"
                        [routerLink]="getRouterLink(item)"
                        lines="none"
                        [class.alt]="item.alt || (item.alt === undefined && i % 2 === 0)"
                        [ngStyle]="item.style"
                        [style.--min-height]="item.height + 'px'"
                        [class.showDelete]="showDelete"
                    >
                        <ng-template
                            [ngTemplateOutlet]="template ?? defaultTemplate"
                            [ngTemplateOutletContext]="{ item: item }"
                        ></ng-template>
                        <div class="itemOptions" *ngIf="showDelete">
                            <ion-icon name="trash" (click)="delete($event, item, itemSliding)"></ion-icon>
                        </div>
                    </ion-item>
                    <ion-item
                        *ngIf="!getRouterLink"
                        (click)="itemClicked(item, i, $event)"
                        (press)="itemLongPressed(item, i)"
                        lines="none"
                        [ngStyle]="item.style"
                        [style.--min-height]="item.height + 'px'"
                        [class.alt]="item.alt || (item.alt === undefined && i % 2 === 0)"
                        [class.active]="(isRowActive && isRowActive(item, i)) || (!!activeStyleField && !!item[activeStyleField])"
                        [class.showDelete]="showDelete"
                    >
                        <ng-template
                            [ngTemplateOutlet]="template ?? defaultTemplate"
                            [ngTemplateOutletContext]="{ item: item }"
                        ></ng-template>
                        <div class="itemOptions" *ngIf="showDelete">
                            <ion-icon name="trash" (click)="delete($event, item, itemSliding)"></ion-icon>
                        </div>
                    </ion-item>
                    <ion-item-options side="end" *ngIf="showDelete">
                        <ion-item-option class="delete" (click)="delete($event, item, itemSliding)" expandable
                            ><ion-icon name="trash"></ion-icon
                        ></ion-item-option>
                    </ion-item-options>
                </ion-item-sliding>
            </div>
        </ion-list>

        <div *ngIf="noItemsTextKey && items?.length === 0" class="page-placeholder content">
            {{ noItemsTextKey | translate }}
        </div>
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                position: relative;
                margin-bottom: 20px;
                margin-top: 10px;
            }

            ion-list,
            ion-item {
                flex: 1 1 auto;
                display: flex;
                flex-direction: column;
                --background: none;
                --ion-item-background: none;
                padding: 0;
            }

            abs-icon {
                margin-right: 10px;
            }

            .row {
                position: absolute;
                transform: translate3d(0, 0, 0);
                display: flex;
                justify-items: end;
                width: 100%;
            }

            .deleted {
                opacity: 0;
                margin-left: -100%;
                transition: all 0.35s;
            }

            .clickable {
                cursor: pointer;
                position: absolute;
            }

            .clickable:hover:before {
                content: '';
                background: rgba(0, 0, 0, 0.1);
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                pointer-events: none;
                z-index: 2;
            }

            .itemOptions {
                display: none;
            }

            @media (min-width: 601px) {
                .showDelete {
                    --inner-padding-end: 0 !important;
                }
                .showDelete .itemOptions {
                    display: flex;
                    padding: 0 15px;
                }
            }
        `
    ]
})
export class DynamicListComponent implements OnChanges, OnDestroy, AfterViewInit {
    @Input() debugId: string;

    @Input() template: TemplateRef<any> = null;
    @Input() headerTemplate: TemplateRef<any> = null;
    @Input() items: any[] = [];
    @Input() noItemsTextKey: string = null;
    @Input() showDelete = true;
    @Input() activeStyleField: string = null;
    @Input() showSearch: boolean = false;
    searchVal = '';
    @Input() isRowActive: (item: any, index: number) => boolean;
    @Input() getRouterLink: (item: any) => string[];
    @Output() itemSelect = new EventEmitter<{ item: any; i: number }>();
    @Output() itemLongPress = new EventEmitter<{ item: any; i: number }>();
    @Output() deleteItem = new EventEmitter<{
        item: any;
        i: number;
        slidingItem: IonItemSliding;
        callback: (deleted: boolean) => void;
    }>();

    firstRender = true;

    @ViewChild('defaultTemplate') defaultTemplate;
    @ViewChild('defaultHeaderTemplate') defaultHeaderTemplate;

    @Input() groupBy: string = null;
    @Input() groupNames: string[];
    @Input() groupNameField: string = null;
    @Input() showEmptyGroups: boolean = false;
    currentHeader: string;

    @ViewChild('virtualScroll') virtualScroll: any;
    @Input() headerHeight = 40;
    @Input() itemHeight = 50;
    @Input() getHeaderHeight = (_: any) => undefined;
    @Input() getItemHeight = (_: any) => undefined;
    @Input() itemHover = true;
    displayRows$ = new ReplaySubject<any[]>();
    displayRows = [];
    allItemsAndHeaders: any[];
    itemListIds: number[]; // Used to find the index of an item being deleted
    offsetIndex: number[];

    listHeight$ = new ReplaySubject<number>(0);
    topOffset: number = 0 - Infinity;

    scrollHeight: number = 1400;
    scrollEl: HTMLElement;
    scrollSub: Subscription;
    lastScrollTop = 0;
    overflowArea = 400;
    ancestorsAndSelf: HTMLElement[];

    destroy$ = new ReplaySubject();

    @Input()
    showAll = false;

    constructor(
        private router: Router,
        private scrollService: ScrollService,
        private route: ActivatedRoute
    ) {}

    trackByFn(_i, item) {
        return item.name + ':' + item.count;
    }

    ngAfterViewInit() {
        retry([0, 10, 100, 250, 500], async () => {
            // A little bit hacky but make sure the items all appear
            this.getScrollAncestorsAndSelf().then((ancestorsAndSelf) => {
                this.initScroll();
            });
        });

        fromEvent(window, 'resize')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.initScroll();
            });
    }

    initScroll() {
        this.getScrollAncestorsAndSelf()
            .then((ancestorsAndSelf) => {
                this.ancestorsAndSelf = ancestorsAndSelf;
                if (!ancestorsAndSelf) {
                    return;
                }
                const nodeNames = ancestorsAndSelf.map((x) => x.nodeName);
                if (nodeNames.includes('ION-CONTENT')) {
                    this.scrollService.outlets[this.route.outlet].scrollEl$.pipe(take(1)).subscribe((scrollEl) => {
                        if (!scrollEl) {
                            return;
                        }
                        this.subscribeToScrollEvents(scrollEl);
                    });
                } else {
                    this.subscribeToScrollEvents(ancestorsAndSelf[ancestorsAndSelf.length - 1]);
                }
                this.updateItems();
            })
            .catch((err) => {});
    }

    subscribeToScrollEvents(scrollEl: HTMLElement) {
        if (this.scrollEl) {
            return;
        }

        console.log(this.debugId, 'subscribeToScrollEvents');
        this.scrollEl = scrollEl;
        fromEvent(scrollEl, 'scroll')
            .pipe(
                tap(() => {
                    console.log('Scroll event detected');
                }),
                takeUntil(this.destroy$),
                filter(() => Math.abs(scrollEl.scrollTop - this.lastScrollTop) > this.itemHeight),
                tap(() => (this.lastScrollTop = scrollEl.scrollTop))
                // throttleTime(17, undefined, { leading: true, trailing: true })
            )
            .subscribe(() => {
                this.scrollHeight = document.body.clientHeight + this.overflowArea;
                this.updateVisibleRows();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        for (const propName in changes) {
            if (changes.hasOwnProperty(propName)) {
                switch (propName) {
                    case 'items': {
                        if (
                            changes.items.currentValue &&
                            changes.items.currentValue !== changes.items.previousValue &&
                            this.topOffset > 0 - Infinity
                        ) {
                            this.updateItems();
                        }
                    }
                }
            }
        }
    }

    filterBySearchValue = (item: any, searchVal: string) => {
        const translatedName = item.name?.en ? getValueFromTranslationSync(item.name) : item.name;
        return !searchVal || translatedName.toUpperCase().includes(searchVal.toUpperCase());
    };

    updateSearch(value: any) {
        this.searchVal = value;
        this.updateItems();
    }

    async updateItems() {
        await this.getTopOffset();
        if (!this.items) {
            return;
        }
        this.currentHeader = '';
        let allItemsAndHeaders = [
            ...this.items
                .filter((x) => !!x && !x.hide)
                .filter((x) => this.filterBySearchValue(x, this.searchVal))
                .sort((a, b) => {
                    if (!this.groupBy) {
                        return 0;
                    }
                    if (this.groupNames?.length > 0) {
                        return this.groupNames.indexOf(a[this.groupBy]) - this.groupNames.indexOf(b[this.groupBy]);
                    }
                    return a[this.groupBy]?.toString().localeCompare(b[this.groupBy]?.toString());
                })
                .map((e, i) => ({
                    ...e,
                    alt: i % 2 === 0,
                    uniqueListId: getUniqueId()
                }))
        ];

        this.itemListIds = allItemsAndHeaders.map((x) => x.uniqueListId);

        if (this.groupBy) {
            let groups = this.groupNames?.map((x) => ({ id: x, name: x }));

            const groupNameField = this.groupNameField || this.groupBy;
            const itemGroups = allItemsAndHeaders.map((x) => ({ id: x[this.groupBy], name: x[groupNameField] }));
            if (!groups || groups.length === 0) {
                groups = itemGroups.filter((g, i) => itemGroups.findIndex((x) => x.id === g.id) === i);
            }

            // Insert groupname rows
            let currentItemIndex = 0;
            for (let i = 0; i < groups.length; i++) {
                const group = groups[i];
                let headersAdded = 0;
                if (group && group.name) {
                    allItemsAndHeaders.splice(currentItemIndex, 0, { isHeader: true, ...group } as any);
                    headersAdded = 1;
                }
                const itemCount = allItemsAndHeaders.filter((x) => x[this.groupBy] === group.id).length;
                currentItemIndex += itemCount + headersAdded;
            }
        }

        let runningTotal = 0;
        allItemsAndHeaders = allItemsAndHeaders.map((x) => {
            const yPos = runningTotal;
            const height = x.isHeader
                ? x.height || this.getHeaderHeight(x) || this.headerHeight
                : x.height || this.getItemHeight(x) || this.itemHeight;
            runningTotal += height;
            return {
                ...x,
                yPos,
                height: height,
                style: {
                    height: height + 'px',
                    'flex-basis': height + 'px'
                }
            };
        });

        const lastItem = allItemsAndHeaders[allItemsAndHeaders.length - 1];
        if (lastItem) {
            this.listHeight$.next(
                lastItem.yPos +
                    (lastItem.isHeader
                        ? lastItem.height || this.getHeaderHeight(lastItem) || this.headerHeight
                        : lastItem.height || this.getItemHeight(lastItem) || this.itemHeight)
            );
        } else {
            this.listHeight$.next(0);
        }

        this.allItemsAndHeaders = allItemsAndHeaders;
        this.updateVisibleRows();
    }

    updateVisibleRows() {
        let displayRows = this.allItemsAndHeaders;
        if (!this.showAll) {
            if (!this.scrollEl) {
                return false;
            }
            const startY = this.scrollEl.scrollTop - this.topOffset - this.overflowArea;
            const endY = startY + this.scrollHeight + this.overflowArea;

            displayRows = this.allItemsAndHeaders.filter((x) => x.yPos > startY && x.yPos < endY);
        }
        this.displayRows$.next(displayRows);
        this.displayRows = displayRows;
        return true;
    }

    getTopOffset(force = false): Promise<number> {
        const intervals = [0, 10, 50, 100, 250, 500, 1000, 2000];
        return retry(
            intervals,
            async (resolve, reject) => {
                if (this.topOffset > 0 - Infinity && !force) {
                    resolve(this.topOffset);
                    return;
                }

                if (!this.ancestorsAndSelf) {
                    reject(this.debugId + ': Cannot calculate top offset without ancestor elements');
                    return;
                }

                const offsetTop: number = this.ancestorsAndSelf.reduce((a, b) => a + b.offsetTop, 0);
                this.topOffset = offsetTop;
                resolve(offsetTop);
                this.initScroll();
            },
            true
        );
    }

    getScrollAncestorsAndSelf() {
        return retry([0, 10, 100, 500, 1000, 2000, 5000], (resolve, reject) => {
            const ancestors = [];
            let el = this.virtualScroll.el;
            if (!el) {
                reject(this.debugId + ': Virtual scroll element not loaded');
                return;
            }
            while (!this.isScrollEl(el)) {
                ancestors.push(el.offsetParent);
                if (!el.offsetParent) {
                    (window as any).tmpEl = el;
                    reject(this.debugId + ': offsetParent not found');
                    return;
                }
                el = el.offsetParent;
            }
            resolve(ancestors);
        }).catch((err) => {
            // swallow error as it's usually because a parent element is hidden
            // (e.g. when not in landscape mode and list is off screen)
            // console.log(err);
        });
    }

    isScrollEl(el: HTMLElement) {
        if (!el) {
            return false;
        }

        let scrollType = el.computedStyleMap()?.get('overflow-y')?.['value'];

        return (
            scrollType === 'auto' ||
            scrollType === 'scroll' ||
            el.nodeName === 'ION-CONTENT' ||
            [...(el.classList as any)].includes('modal') ||
            [...(el.classList as any)].includes('scrollParent')
        );
    }

    itemClicked(item: any, i: number, e: MouseEvent) {
        if (e.ctrlKey || e.altKey || e.shiftKey) {
            this.itemLongPressed(item, i);
        } else {
            this.itemSelect.emit({ item: item, i });
        }
    }

    itemLongPressed(item: any, i: number) {
        this.itemLongPress.emit({ item: item, i });
    }

    isNaN(value: any) {
        return isNaN(value);
    }

    async delete(e: Event, item: any, slidingItem: IonItemSliding) {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        await slidingItem.close();

        const itemOnlyIndex = this.itemListIds.indexOf(item.uniqueListId);

        const setItemDeleted = (deleted: boolean) => {
            item.deleted = deleted;
            const allRowsIndex = this.displayRows.indexOf(item);
            this.displayRows[allRowsIndex].deleted = deleted;
            this.displayRows$.next(this.displayRows);
        };
        setItemDeleted(true);
        setTimeout(() => {
            this.deleteItem.emit({ item: item, i: itemOnlyIndex, slidingItem, callback: setItemDeleted });
        }, 350);
    }

    ngOnDestroy() {
        console.log(this.debugId, 'ngOnDestroy');
        this.destroy$.next(true);
    }
}

// export interface NamedItem {
//     name: Translation;
//     isHeader?: number;
//     yPos: number;
//     height: number;
//     alt?: boolean;
//     deleted?: boolean; // used to animate a deleted item disappearing
//     hide?: boolean;
// }
